import { createContext, useContext, useLayoutEffect, useCallback, useMemo, useRef, useState, type ReactNode } from "react";
import { MantineProvider } from "@mantine/core";
import {
  MOTIONS,
  PREF_KEYS,
  TABLE_MODES,
  THEME_IDS,
  readPref,
  writePref,
  type Motion,
  type TableMode,
  type ThemeId,
} from "./prefs";
import { APP_THEMES, DEFAULT_THEME } from "./themes";

interface AppearanceValue {
  theme: ThemeId;
  setTheme(id: ThemeId): void;
  tableMode: TableMode;
  setTableMode(m: TableMode): void;
  motion: Motion;
  setMotion(m: Motion): void;
  effectiveTheme: ThemeId;
}

interface InternalValue extends AppearanceValue {
  pushForced(id: ThemeId): () => void;
}

const Ctx = createContext<InternalValue | null>(null);

export function AppearanceProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<ThemeId>(() => readPref(PREF_KEYS.theme, THEME_IDS, DEFAULT_THEME));
  const [tableMode, setTableModeState] = useState<TableMode>(() =>
    readPref(PREF_KEYS.tableMode, TABLE_MODES, "compact"),
  );
  const [motion, setMotionState] = useState<Motion>(() => readPref(PREF_KEYS.motion, MOTIONS, "on"));
  const [forced, setForced] = useState<{ key: number; id: ThemeId }[]>(() =>
    // Public /s/ pages force the "clean" theme, but only once the route mounts (App first shows the
    // auth-loading spinner): seed it so the saved theme is never painted before that. The seed (key -1)
    // is replaced by the page's own useForcedTheme.
    typeof window !== "undefined" && window.location.pathname.startsWith("/s/") ? [{ key: -1, id: "clean" }] : [],
  );

  const effectiveTheme: ThemeId = forced.length ? forced[forced.length - 1].id : theme;
  const def = APP_THEMES[effectiveTheme] ?? APP_THEMES[DEFAULT_THEME];

  useLayoutEffect(() => {
    const el = document.documentElement;
    el.setAttribute("data-app-theme", effectiveTheme);
    el.setAttribute("data-motion", motion);
  }, [effectiveTheme, motion]);

  const counter = useRef(0);
  const pushForced = useCallback((id: ThemeId) => {
    const key = ++counter.current;
    setForced((s) => [...s.filter((f) => f.key !== -1), { key, id }]);
    return () => setForced((s) => s.filter((f) => f.key !== key));
  }, []);

  const value = useMemo<InternalValue>(() => {
    return {
      theme,
      tableMode,
      motion,
      effectiveTheme,
      setTheme: (id) => {
        setThemeState(id);
        writePref(PREF_KEYS.theme, id);
      },
      setTableMode: (m) => {
        setTableModeState(m);
        writePref(PREF_KEYS.tableMode, m);
      },
      setMotion: (m) => {
        setMotionState(m);
        writePref(PREF_KEYS.motion, m);
      },
      pushForced,
    };
  }, [theme, tableMode, motion, effectiveTheme, pushForced]);

  return (
    <Ctx.Provider value={value}>
      <MantineProvider theme={def.mantine} cssVariablesResolver={def.vars} forceColorScheme={def.scheme}>
        <div className="app-bg-fx" aria-hidden />
        {children}
      </MantineProvider>
    </Ctx.Provider>
  );
}

export function useAppearance(): AppearanceValue {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAppearance must be used inside AppearanceProvider");
  return v;
}

/** While mounted, forces the effective theme to `id` without changing the saved preference. */
export function useForcedTheme(id: ThemeId): void {
  const v = useContext(Ctx);
  const push = v?.pushForced;
  // Layout effect: the setState it triggers re-renders synchronously before paint, and the
  // provider's layout effect then rewrites data-app-theme with the forced value, so the saved
  // theme is never painted.
  useLayoutEffect(() => (push ? push(id) : undefined), [push, id]);
}
